import { useCallback } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { DoseDetailView } from '~/domain/projections/views';
import { useDoseDetailView } from '~/hooks/domain/useDoseDetailView';
import { useStoreResolvedEntity } from '~/hooks/domain/useStoreResolvedEntity';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { currentDoseIdState } from '~/state/atoms/dose/doseAtoms';

/**
 * Resolves the selected {@link DoseId} to the live {@link DoseMatrix} in
 * the patient aggregate. Returns `undefined` when nothing is selected, no
 * patient is loaded, or the id no longer resolves (the dose was deleted
 * while selected — the reference can no longer dangle, which is the point
 * of holding an id rather than the entity).
 *
 * Prefer `useDoseDetailView()` / `useDoseListView()` where a read-only
 * projection suffices. Reach for the entity only where the live object is
 * genuinely needed — pixel data for the engine, DVH calculation, or the
 * cached `doseMaxPosition`. Never mutate what this returns: dose writes go
 * through `DoseService`, which wraps them in a tracked command.
 */
export function useCurrentDose(): DoseMatrix | undefined {
    const patient = useSelectedPatient();
    const doseId = useRecoilValue(currentDoseIdState);
    // Re-resolve on every changeset (so an in-place removal is observed
    // immediately) but re-render only when the resolved dose reference
    // actually changes. Subscribing to the raw domain version instead would
    // re-render every current-dose consumer — viewport grid, table panel,
    // overlays, dose sidebar — on every unrelated changeset. See
    // useStoreResolvedEntity.
    return useStoreResolvedEntity(() =>
        patient && doseId
            ? patient.doses.find((d) => d.id === doseId)
            : undefined
    );
}

/**
 * Read-side projection of the selected dose, or `undefined` when nothing
 * is selected. This is the replacement for the former `doseDataState`
 * atom: the same fields, but derived from the DomainStore version instead
 * of being written into Recoil by hand on every dose mutation.
 */
export function useCurrentDoseView(): DoseDetailView | undefined {
    const doseId = useRecoilValue(currentDoseIdState);
    return useDoseDetailView(doseId);
}

/**
 * Setter counterpart to {@link useCurrentDose}. Accepts the entity (or
 * `undefined` to clear) and stores only its id, so call sites that used to
 * write the entity into Recoil need no restructuring.
 */
export function useSetCurrentDose(): (dose: DoseMatrix | undefined) => void {
    const setDoseId = useSetRecoilState(currentDoseIdState);
    return useCallback(
        (dose: DoseMatrix | undefined) => {
            setDoseId(dose?.id ?? null);
        },
        [setDoseId]
    );
}
